// drip-audience-step.tsx — the REAL audience picker + import/edit, reskinned
// for the Drip Campaigns redesign.
//
// Reuses the existing campaign audience surface wholesale (BulkAudiencesList +
// BulkAudienceSelectRow: search, list, "New audience" import dialog, per-row
// select checkbox + Edit / Duplicate / Delete menu).
//
// OWNERSHIP (§2 of the state-flow handoff): the wizard's selected audiences are
// the wizard's LOCAL `selected` state — that is the source of truth handed to
// buildDrip. The reused checkbox row only knows how to write
// `newCampaign.boardIDArray`, so we treat that legacy key as a TRANSIENT event
// channel: seed it once from `selected` on entry, read changes back UP into the
// wizard, and RESET it on unmount. The list's mode (selection checkboxes) is
// owned by the AudienceListAdapter, which sets it on mount and restores the
// prior global mode on unmount — so this step can never leave the list's own
// Audiences tab flipped into checkbox-selection mode.
//
// A drip targets N audiences (#161). The row checkbox already toggles boards in
// and out of boardIDArray properly; this step used to reduce that array to its
// LAST entry on the way up, which is what silently collapsed a five-audience
// campaign to one. It now mirrors the whole set.

import React from 'react';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { setNewCampaign } from '@common/reducers/CampaignReducer';
import type { CommonRootState } from '@common/types/common-root-state-type';

import { AudienceListAdapter } from './campaign-state-bridge';
import { sameAudienceIds, toAudienceIds } from './audience-selection';
import { StepBadge } from './wizard-step-chrome';

interface DripAudienceStepProps {
  /** Currently selected audience board ids (the wizard's SoT). */
  selected: string[];
  onSelect: (boardIds: string[]) => void;
}

export const DripAudienceStep: React.FC<DripAudienceStepProps> = ({ selected, onSelect }) => {
  const dispatch = useAppDispatch();
  // Re-armed by the seed effect's cleanup so a remount (StrictMode's double-invoke,
  // or simply re-entering the step) skips its first mirror run again.
  const mirrorArmed = React.useRef(false);

  // Seed the transient selection channel from the wizard's local SoT on entry,
  // and clear it on exit so nothing about the in-progress drip lingers in the
  // global newCampaign (§2). Mode (checkbox selection) is the adapter's job.
  React.useEffect(() => {
    dispatch(setNewCampaign({ boardIDArray: selected }));
    return () => {
      mirrorArmed.current = false;
      dispatch(setNewCampaign({ boardIDArray: [] }));
    };
    // Seed once on mount; subsequent changes flow row → boardIDArray → up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Mirror the transient boardIDArray back to the wizard's SoT — the WHOLE set,
  // so ticking a second audience adds to the campaign instead of superseding the
  // first. Compared set-wise so a mere reorder can't ping-pong patch ↔ re-seed.
  const boardIDArray = useAppSelector((s: CommonRootState) => s.CampaignReducer.newCampaign?.boardIDArray);
  React.useEffect(() => {
    // SKIP the first run. Both effects belong to the same commit, so this one
    // sees the boardIDArray captured BEFORE the seed above dispatched — i.e. the
    // leftover global value (normally [] from the previous visit's cleanup), never
    // anything the user chose here. Mirroring it would immediately patch the draft
    // to "no audiences" and only restore on the next pass; leave the step in
    // between and the draft has genuinely lost them.
    if (!mirrorArmed.current) {
      mirrorArmed.current = true;
      return;
    }
    const next = toAudienceIds(boardIDArray);
    if (!sameAudienceIds(next, selected)) onSelect(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardIDArray]);

  return (
    <div className="fade-in campaigns-rd-audience" style={{ maxWidth: 760, margin: '0 auto' }}>
      {/* The reused BulkAudiencesList (rendered by AudienceListAdapter) renders
          its own centred "Who to send?" title + subtitle, so we only add the
          step badge above it — matching the badge+title+subtitle shape the other
          wizard steps get from StepHeading. */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
        <StepBadge step="audience" />
      </div>
      <AudienceListAdapter mode="selection" />
    </div>
  );
};
